// vendor modules
import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { Tooltip } from "@quorumus/plum"

// components
import { SelectableSubheader, Icon } from "quorumdesign"

// helper functions
import { useResponsiveSize } from "@/hooks"
import { filterHasValue } from "shared/search/helperFunctions"

// Redux related
import { updateFilter } from "shared/search/action-creators"
import {
    selectFilterValue,
    selectIsRegisteredFilter,
    selectIsPermanentFilter,
    selectIsFilterGroupOpen,
} from "shared/search/selectors"

type FilterValue = string | number | boolean | null | Record<string, any>

interface SearchFilterProps {
    dataTypeLabel?: string

    // Convert the string serialized value back into its true datatype
    deserialize: (value: string) => FilterValue
    displayLabel: string
    filterKey: string
    helpText?: string

    // The initial value should be the post-serialization starting value of the filter
    initialValue?: string
    invisible?: boolean
    isNullable?: boolean
    isProtected?: boolean
    isSubfilter?: boolean
    isSubSubfilter?: boolean
    presentationComponent: React.ElementType
    searchFilterTerm?: string // selected from the search => props.uniqueFlvId slice of the store

    // Convert the true value datatype into a string serialized version
    serialize: (value: FilterValue, filterKey?: string) => string

    // from app/static/frontend/search-new/components/FilterGroup/index.js renderFilters
    setFilterGroupContainsSearchFilterTerm: (arg0: boolean) => void
    shouldAlwaysDisplayFilter: boolean
    uniqueFilterId: string
    uniqueFlvId: string
}

const UnconnectedSearchFilter = ({
    displayLabel,
    initialValue,
    isNullable = true,
    isProtected = false,
    searchFilterTerm,
    setFilterGroupContainsSearchFilterTerm,
    uniqueFilterId,
    uniqueFlvId,
    updateFilter,
    value,
    ...props
}: SearchFilterProps & StateProps & DispatchProps) => {
    const [filterContainsSearchFilterTerm, setFilterContainsSearchFilterTerm] = useState(false)
    const [hideFilter, setHideFilter] = useState(false)

    const neverHideFilter = props.isSubfilter || props.shouldAlwaysDisplayFilter

    // Searching Within Filters - if the search filter term is set, we want to hide any filters that don't match
    useEffect(() => {
        if (!searchFilterTerm) {
            setFilterContainsSearchFilterTerm(false)
            setHideFilter(false)
        } else {
            if (
                (displayLabel && displayLabel.toString().toLowerCase().includes(searchFilterTerm.toLowerCase())) ||
                filterContainsSearchFilterTerm
            ) {
                setFilterGroupContainsSearchFilterTerm(true)
                setHideFilter(false)
            } else {
                setHideFilter(true)
            }
        }
    }, [displayLabel, searchFilterTerm, filterContainsSearchFilterTerm, setFilterGroupContainsSearchFilterTerm])

    // If we have a null value on a non-nullable filter, we want to set the value to the initial value
    useEffect(() => {
        if (!filterHasValue(value) && !isNullable) {
            updateFilter(uniqueFilterId, uniqueFlvId, initialValue, true)
        }
    }, [value, isNullable, initialValue, uniqueFilterId, uniqueFlvId, updateFilter])

    useEffect(
        () => {
            if (initialValue && !filterHasValue(value)) {
                updateFilter(uniqueFilterId, uniqueFlvId, initialValue, true)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            /* intentionally empty so this runs only on first render */
        ],
    )

    const publishChange = (value) => {
        const serializedValue = props.serialize
            ? // pass filterKey in so that datePickerGenericFilter can serialize value based on the filterKey
              props.serialize(value, props.filterKey)
            : value
        return updateFilter(uniqueFilterId, uniqueFlvId, serializedValue, filterHasValue(serializedValue))
    }

    const PresentationComponent = props.presentationComponent
    const deserializedValue = props.deserialize ? props.deserialize(value) : value

    const { isMobile } = useResponsiveSize()
    const showTooltip = props.helpText && !isMobile

    return (
        <div
            className={!props.isSubSubfilter ? "filter-header-design-system" : ""}
            style={{
                ...(!neverHideFilter && hideFilter && { display: "none" }),
                ...(props.isSubSubfilter && {
                    minWidth: 0,
                    width: "100%",
                }),
            }}
        >
            <SelectableSubheader
                invisible={props.invisible}
                searchFilterTerm={searchFilterTerm}
                title={displayLabel}
                children={
                    showTooltip && (
                        <Tooltip label={props.helpText}>
                            <Icon icon="info-circle" iconFamily="far" color="#7B8A8B" />
                        </Tooltip>
                    )
                }
            />
            <PresentationComponent
                // FOR DISCUSSION - "Explicit Prop Spreading"
                // see https://medium.com/@taitasciore/explicit-props-spreading-in-react-components-acc3eb64394c
                {...{
                    deserializedValue,
                    displayLabel,
                    initialValue,
                    isNullable,
                    isProtected,
                    publishChange,
                    searchFilterTerm,
                    setFilterContainsSearchFilterTerm,
                    setFilterGroupContainsSearchFilterTerm,
                    uniqueFilterId,
                    uniqueFlvId,
                    updateFilter,
                    value,
                }}
                {...props}
            />
        </div>
    )
}

interface StateProps {
    filterGroupIsOpen: boolean
    isPermanent: boolean
    isRegistered: boolean
    value?: string
}

interface DispatchProps {
    updateFilter: typeof updateFilter
}

const mapStateToProps = (state, props) => ({
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    value: selectFilterValue(state, props),
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    isRegistered: selectIsRegisteredFilter(state, props),
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    isPermanent: selectIsPermanentFilter(state, props),
    // @ts-expect-error These selectors are improperly typed with the current version of react-redux
    filterGroupIsOpen: selectIsFilterGroupOpen(state, props),
})

export const SearchFilter = connect<StateProps, DispatchProps, SearchFilterProps>(mapStateToProps, { updateFilter })(
    UnconnectedSearchFilter,
)
